import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersApi } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Users } from 'lucide-react';

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}

export default function FollowersModal({ username, type, open, onClose }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !username) return;
    setLoading(true);
    const fetch = type === 'followers' ? usersApi.getFollowers : usersApi.getFollowing;
    fetch(username)
      .then((data) => setUsers(data || []))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, [open, username, type]);

  const title = type === 'followers' ? 'Followers' : 'Following';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-cream border-cream-dark max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-ink">{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-1 max-h-96 overflow-y-auto">
          {loading ? (
            <>
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-1 py-2">
                  <Skeleton className="w-9 h-9 rounded-full bg-cream-dark" />
                  <div className="space-y-1.5 flex-1">
                    <Skeleton className="h-3.5 w-28 bg-cream-dark" />
                    <Skeleton className="h-3 w-20 bg-cream-dark" />
                  </div>
                </div>
              ))}
            </>
          ) : users.length === 0 ? (
            <div className="text-center py-10">
              <Users className="w-10 h-10 text-ink-muted mx-auto mb-3 opacity-40" />
              <p className="text-ink-muted text-sm">
                {type === 'followers' ? 'No followers yet.' : 'Not following anyone yet.'}
              </p>
            </div>
          ) : (
            users.map((u) => (
              <button
                key={u.username}
                onClick={() => { onClose(); navigate(`/profile/${u.username}`); }}
                className="w-full flex items-center gap-3 px-1 py-2 rounded-lg hover:bg-cream-dark/60 transition-colors text-left"
              >
                <Avatar className="w-9 h-9 flex-shrink-0">
                  <AvatarImage src={u.avatar_url} alt={u.display_name} />
                  <AvatarFallback className="bg-terra text-cream text-xs font-semibold">
                    {getInitials(u.display_name || u.username)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ink truncate">{u.display_name || u.username}</p>
                  <p className="text-xs text-ink-muted">@{u.username}</p>
                </div>
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
